import Header from './Header';
import _ from 'lodash';
import { Checkbox, Tabs, TextField } from '@scale/scaleui';
import { EmailDripCampaignTable } from './EmailDripCampaignTable';
import React, { useState, useMemo, useEffect } from 'react';
import { getLifecycleCommTemplates, listEmailDripCampaign } from 'react-scale/corp/lib/api/growth';
import {
  EmailDripCampaignListTab,
  ALL_EMAIL_DRIP_CAMPAIGN_QUERY_KEY,
  YOUR_EMAIL_DRIP_CAMPAIGN_QUERY_KEY,
  LIFECYCLE_COMM_TEMPLATES_QUERY_KEY,
} from 'react-scale/components/GrowthCenter/constants';
import { useQuery } from 'react-query';
import { IUserJson } from '@scale/shared/types/user';
import { EmailDripCampaign } from '@scale/shared-types';
import { useHistory, useLocation } from 'react-router-dom';

const EmailDripCampaignPage: React.FC<{ user: IUserJson }> = ({ user }) => {
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const tabFromUrl = queryParams.get('tab');

  const tabFromUrlDecoded = tabFromUrl
    ? tabFromUrl
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    : null;

  const [currentTab, setCurrentTab] = useState<string>(
    tabFromUrlDecoded &&
      Object.values(EmailDripCampaignListTab).includes(
        tabFromUrlDecoded as EmailDripCampaignListTab,
      )
      ? tabFromUrlDecoded
      : (_.first(Object.values(EmailDripCampaignListTab)) as string),
  );
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showScheduledOnly, setShowScheduledOnly] = useState<boolean>(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabForUrl = currentTab.toLowerCase().replace(/\s+/g, '-');
    params.set('tab', tabForUrl);
    history.replace({ search: params.toString() });
  }, [currentTab, history]);

  const onTabChange = (val: string) => {
    setCurrentTab(val as EmailDripCampaignListTab);
  };

  const userId = user._id ?? user.id;

  const yourCampaigns = useQuery([YOUR_EMAIL_DRIP_CAMPAIGN_QUERY_KEY], () =>
    listEmailDripCampaign(userId),
  );
  const allCampaigns = useQuery([ALL_EMAIL_DRIP_CAMPAIGN_QUERY_KEY], () => listEmailDripCampaign());
  const lifecycleCommTemplates = useQuery([LIFECYCLE_COMM_TEMPLATES_QUERY_KEY], () =>
    getLifecycleCommTemplates(),
  );

  const currCampaigns = useMemo(
    () =>
      currentTab === EmailDripCampaignListTab.YourCommunications ? yourCampaigns : allCampaigns,
    [currentTab, allCampaigns, yourCampaigns],
  );

  const filteredData = useMemo(() => {
    if (currCampaigns.isLoading) return [];
    const data = currCampaigns.data?.filter(
      (campaign: EmailDripCampaign) => !showScheduledOnly || campaign.sendAt,
    );
    if (data) {
      if (searchTerm.length > 0) {
        return data.filter((campaign: EmailDripCampaign) =>
          Object.values(campaign).some(
            (value: any) =>
              value && value.toString().toLowerCase().includes(searchTerm.toLowerCase()),
          ),
        );
      }
      return data;
    }
    return [];
  }, [currCampaigns, searchTerm, showScheduledOnly]);

  return (
    <div className="p-8 flex flex-col gap-4">
      <Header currentTab={currentTab} />
      <Tabs
        onChange={onTabChange}
        tabs={Object.values(EmailDripCampaignListTab).map(tab => ({ id: tab }))}
        value={currentTab}
        variant="default"
      />
      {currentTab !== EmailDripCampaignListTab.LifecycleCommunications && (
        <TextField
          className="w-[35%]"
          leadingIcon="search"
          id="search"
          value={searchTerm}
          onChange={(e: any) => {
            setSearchTerm(e.target.value);
          }}
          placeholder={'Search by campaign name, source, pod, or creator...'}
        />
      )}
      {currentTab === EmailDripCampaignListTab.LifecycleCommunications && (
        <div>Metrics only shown for the first template in the delivery schedule</div>
      )}
      {currentTab !== EmailDripCampaignListTab.LifecycleCommunications && (
        <Checkbox
          checked={showScheduledOnly}
          onChange={e => setShowScheduledOnly(e.target.checked)}
          label="Show Scheduled Broadcasts Only"
        />
      )}
      <EmailDripCampaignTable
        data={filteredData}
        isLoading={currCampaigns.isLoading || lifecycleCommTemplates.isLoading}
        allData={allCampaigns.data ?? []}
        currentTab={currentTab}
        lifecycleCommTemplates={lifecycleCommTemplates.data ?? []}
      />
    </div>
  );
};

export default EmailDripCampaignPage;
